import {Icon, Link, Divider, view} from '@sail/ui';
import {support} from '@sail/icons/react/Icon';

import {
  ConversationArticle,
  Conversation,
} from '@stripe-internal/conversations-client';
import {useCallback} from 'react';
import {defineMessages, FormattedMessage} from 'react-intl';
import {useAnalytics} from '../../../AnalyticsProvider';
import {useTheme} from '../../../ThemeProvider';

const messages = defineMessages({
  article: {
    id: 'conversations_ui.conversation.log.messages.articles.article',
    description: 'Label in card indicating kind of content (article)',
    defaultMessage: 'Article',
  },
  support: {
    id: 'conversations_ui.conversation.log.messages.articles.support',
    description: 'Label in card indicating kind of article',
    defaultMessage: 'Support',
  },
});

type Props = {
  conversation: Conversation;
  article: ConversationArticle;
};

export const ArticleNext = ({conversation, article}: Props) => {
  const analytics = useAnalytics();

  const onPress = useCallback(() => {
    analytics.action('presented_article_clicked', {
      articleUrl: article.url,
    });
    conversation.action({name: 'article-visited', data: article});
  }, [analytics, article, conversation]);

  const theme = useTheme();

  return (
    /**
     * Using article.url as target instead of '_blank' to prevent opening multiple
     * tabs of the same article from the support widget. If the article has
     * already been opened from here, the already-opened tab will be focused.
     */
    <Link
      data-testid="article-link"
      href={article.url}
      target={article.url}
      onPress={onPress}
      type="secondary"
      css={{
        width: 'fill',
        textDecoration: 'none',
      }}
    >
      <view.div
        className="attachment-info"
        css={{
          position: 'relative',
          border: '1px solid',
          borderColor: 'border',
          borderRadius: '8px',
          padding: 'space.150',
          width: 'fill',
          ':hover': {boxShadow: 'large'},
        }}
      >
        <view.div css={{stack: 'x', alignY: 'center', alignX: 'start'}}>
          <view.span
            css={{
              font: 'label.small.emphasized',
              textColor: theme.articleHeadingsColor,
            }}
          >
            <Icon
              size="xsmall"
              icon={support}
              css={{
                marginRight: 'xsmall',
                color: theme.articleHeadingsColor,
              }}
            />
            <view.span>
              <FormattedMessage {...messages.article} />
            </view.span>
          </view.span>
          <Divider css={{marginX: 'small'}} />
          <view.span
            css={{
              font: 'label.small',
              textColor: theme.articleHeadingsColor,
            }}
          >
            <FormattedMessage {...messages.support} />
          </view.span>
        </view.div>
        <view.div
          css={{
            color: theme.articleLinkColor,
            font: 'label.medium.emphasized',
            marginTop: 'xsmall',
          }}
        >
          {article.title}
        </view.div>
      </view.div>
    </Link>
  );
};
